import { changeCart } from '@theme/ora-cart-helpers';
import { formatMoney } from '@theme/money-formatting';

/**
 * "1 tub or 2 tubs" for `blocks/ora-tub-choice.liquid`.
 *
 * One tub changes nothing: Horizon's flavor chips, stepper and Add button do
 * what they always do, and this only keeps the two tub cards priced. Two tubs
 * takes the buy box over:
 *
 * - **The chips.** The block's own chips replace Horizon's (CSS hides those
 *   while `data-tubs` is "2"). A tap ticks, a second tap unticks; with two
 *   ticked the rest are `aria-disabled` until one is unticked. Same flavor
 *   twice is the link under the bar, and then the one chip carries "×2".
 *   The flavor the buy box was on comes across ticked; nothing else is.
 * - **The prices.** The tub cards and the purchase-option cards of
 *   `blocks/ora-purchase-options.liquid` are rewritten for the pair, and put
 *   back exactly as they were on the way out. Every figure is worked out from
 *   the variants' own prices and Subscribe & Save allocations, in minor units,
 *   so nothing is typed.
 * - **The plan.** A two-tub subscription moves the frequency to the
 *   "Two-tub plan" setting (45 days) with a note, and goes back to what it was
 *   when the shopper returns to one tub -- unless they changed it themselves.
 * - **The add.** The Add button's click is taken in the capture phase, the
 *   pattern `ora-purchase-options.js` established: Horizon builds its request
 *   from the form at click time, so a submit listener would be too late. Two
 *   flavors go in as two lines, the same flavor twice as one line at
 *   quantity 2, each carrying the hidden `_bundle` property the discount
 *   reads. One flavor ticked adds one tub with no property, so the button is
 *   never a dead end. The write goes through `changeCart`, so Horizon opens
 *   and draws the drawer itself.
 *
 * Horizon re-renders the section on a flavor change, which resets this block
 * to one tub; that can only happen while Horizon's own chips are showing, so
 * there is no two-tub state for it to lose.
 */

/**
 * @typedef {object} TubVariant
 * @property {number} id
 * @property {string} name
 * @property {string} color
 * @property {boolean} soldout
 * @property {number} price - one-time, minor units
 * @property {number} sub - Subscribe & Save, minor units
 */

class OraTubChoice extends HTMLElement {
  /** @type {TubVariant[]} */
  variants = [];
  /** @type {number[]} variant ids ticked, in the order ticked */
  picks = [];
  /** @type {{ sub?: string, subTag?: string | null, once?: string } | null} */
  saved = null;
  /** The frequency before two tubs moved it, and the one we moved it to. */
  planBefore = '';
  planOurs = '';
  /** Horizon's own Add label, kept so one tub can put it back. */
  labelBefore = '';
  busy = false;

  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || '[]');
    } catch {
      this.variants = [];
    }

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDocChange = this.onDocChange.bind(this);
    this.onDocClick = this.onDocClick.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onDocChange);
    document.addEventListener('click', this.onDocClick, true);

    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('change', this.onDocChange);
    document.removeEventListener('click', this.onDocClick, true);
  }

  /* ---- where things are ---- */

  /** @returns {HTMLFormElement | null} */
  get form() {
    const form = document.getElementById(this.dataset.form || '');
    return form instanceof HTMLFormElement ? form : null;
  }

  /** @returns {HTMLElement | null} */
  get options() {
    const el = document.querySelector(`ora-purchase-options[data-form="${this.dataset.form}"]`);
    return el instanceof HTMLElement ? el : null;
  }

  /** @returns {HTMLButtonElement | null} */
  get addButton() {
    const form = this.form;
    if (!form) return null;
    const button = Array.from(form.querySelectorAll('button[name="add"]')).find(
      (b) => b instanceof HTMLButtonElement && b.form === form
    );
    return button instanceof HTMLButtonElement ? button : null;
  }

  get tubs() {
    return this.dataset.tubs === '2' ? 2 : 1;
  }

  /** @returns {'subscribe' | 'once'} */
  get mode() {
    const options = this.options;
    if (!options || !options.querySelector('select[data-plan]')) return 'once';
    return options.dataset.mode === 'once' ? 'once' : 'subscribe';
  }

  get percent() {
    const value = Number(this.dataset.percent);
    return Number.isFinite(value) ? value : 0;
  }

  /** @param {number | string} id */
  variant(id) {
    return this.variants.find((v) => String(v.id) === String(id)) || null;
  }

  currentVariant() {
    const input = this.form?.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement ? this.variant(input.value) : null;
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(Math.round(cents), this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /* ---- the sums ---- */

  /** The two tubs being priced: the picks, the one pick twice, or the flavor on show twice. */
  pair() {
    const picked = this.picks.map((id) => this.variant(id)).filter(Boolean);
    if (picked.length === 2) return /** @type {TubVariant[]} */ (picked);
    const one = picked[0] || this.currentVariant() || this.variants.find((v) => !v.soldout);
    return one ? [one, one] : [];
  }

  /**
   * @param {'subscribe' | 'once'} mode
   * @returns {{ full: number, paid: number, save: number }}
   */
  sums(mode) {
    const pair = this.pair();
    const full = pair.reduce((total, v) => total + v.price, 0);
    // Shopify takes a percentage off each unit and rounds that unit's
    // discount down, so $47.99 less 10% is $43.20 in the cart, not $43.19.
    // The page prices the pair the same way so the two agree to the cent.
    const paid = pair.reduce((total, v) => {
      const unit = mode === 'subscribe' ? v.sub : v.price;
      return total + unit - Math.floor((unit * this.percent) / 100);
    }, 0);
    return { full, paid, save: full - paid };
  }

  /** @param {'subscribe' | 'once'} mode @param {number} paid */
  shipsFree(mode, paid) {
    const threshold = Number(mode === 'subscribe' ? this.dataset.freeSub : this.dataset.freeOnce);
    return Number.isFinite(threshold) && threshold > 0 && paid >= threshold;
  }

  /* ---- events ---- */

  /** @param {Event} event */
  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.classList.contains('ora-tubs__radio')) return;
    this.setTubs(target.value === '2' ? 2 : 1);
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const same = target.closest('[data-same]');
    if (same) {
      if (this.picks.length === 1) this.picks = [this.picks[0], this.picks[0]];
      this.update();
      return;
    }

    const chip = target.closest('[data-chip]');
    if (!(chip instanceof HTMLButtonElement) || chip.disabled) return;
    const id = Number(chip.dataset.chip);

    if (this.picks.includes(id)) {
      // A second tap always unticks, and unticks both tubs of a doubled flavor.
      this.picks = this.picks.filter((p) => p !== id);
    } else if (this.picks.length < 2) {
      this.picks = [...this.picks, id];
    } else {
      return;
    }
    this.update();
  }

  /** The purchase option or its frequency changed: the prices follow. */
  onDocChange(event) {
    const target = event.target;
    const options = this.options;
    if (!(target instanceof Node) || !options || !options.contains(target)) return;
    if (target instanceof HTMLSelectElement && this.tubs === 2 && target.value !== this.planOurs) {
      // They chose a frequency themselves; leaving two tubs must not undo it.
      this.planOurs = '';
      this.planBefore = '';
    }
    // ora-purchase-options writes its mode in its own change handler, which
    // may run after this one.
    queueMicrotask(() => this.update());
  }

  /** @param {MouseEvent} event */
  onDocClick(event) {
    if (this.tubs !== 2) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button[name="add"]');
    const form = this.form;
    if (!(button instanceof HTMLButtonElement) || !form || button.form !== form) return;

    event.preventDefault();
    event.stopPropagation();
    if (this.busy) return;

    if (this.picks.length === 0) {
      this.querySelector('[data-chip]:not([disabled])')?.focus();
      return;
    }
    this.add(button);
  }

  /* ---- state ---- */

  /** @param {1 | 2} tubs */
  setTubs(tubs) {
    if (tubs === this.tubs) return;
    this.dataset.tubs = String(tubs);

    if (tubs === 2) {
      const current = this.currentVariant();
      this.picks = current && !current.soldout ? [current.id] : [];
      this.moveToTwoTubPlan();
    } else {
      this.picks = [];
      this.restorePlan();
    }
    this.update();
  }

  moveToTwoTubPlan() {
    const select = this.options?.querySelector('select[data-plan]');
    const want = (this.dataset.plan || '').trim().toLowerCase();
    if (!(select instanceof HTMLSelectElement) || !want) return;
    const option = Array.from(select.options).find((o) => o.text.trim().toLowerCase() === want);
    if (!option || option.value === select.value) return;
    this.planBefore = select.value;
    this.planOurs = option.value;
    select.value = option.value;
  }

  restorePlan() {
    const select = this.options?.querySelector('select[data-plan]');
    if (select instanceof HTMLSelectElement && this.planBefore && select.value === this.planOurs) {
      select.value = this.planBefore;
    }
    this.planBefore = '';
    this.planOurs = '';
  }

  update() {
    const tubs = this.tubs;
    const mode = this.mode;
    const picks = this.picks.length;
    this.dataset.picks = tubs === 2 ? String(picks) : '';

    this.renderCards(mode);
    if (tubs === 2) this.renderPicker(picks);
    this.renderOptions(tubs);
    this.renderButton(tubs, mode, picks);
  }

  /** @param {'subscribe' | 'once'} mode */
  renderCards(mode) {
    const current = this.currentVariant() || this.pair()[0];
    const one = this.querySelector('[data-one-price]');
    if (one && current) one.textContent = this.money(mode === 'subscribe' ? current.sub : current.price);

    const { paid, save } = this.sums(mode);
    const each = this.querySelector('[data-two-each]');
    if (each) each.textContent = this.money(paid / 2);
    const saveLine = this.querySelector('[data-two-save]');
    if (saveLine) {
      // With no extra percentage of its own (a partner page, where the
      // partner's code makes the saving) a one-time pair saves nothing here,
      // so the line says only what is true of it.
      const parts = [];
      if (save > 0) parts.push(`Save ${this.money(save)}`);
      if (this.shipsFree(mode, paid)) parts.push(parts.length ? 'ships free' : 'Ships free');
      saveLine.textContent = parts.join(' · ');
    }
  }

  /** @param {number} picks */
  renderPicker(picks) {
    const first = this.variant(this.picks[0]);
    const doubled = picks === 2 && this.picks[0] === this.picks[1];

    const status = this.querySelector('[data-status]');
    if (status) {
      status.textContent =
        picks === 2 ? 'Both picked. Tap one to swap.' : picks === 1 ? 'Pick 1 more flavor' : 'Pick 2 flavors';
    }
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = `${picks} of 2 picked`;
    this.querySelectorAll('[data-seg]').forEach((seg, i) => seg.toggleAttribute('data-on', i < picks));

    const same = this.querySelector('[data-same]');
    if (same instanceof HTMLElement) {
      same.hidden = !(picks === 1 && first);
      if (first) same.textContent = `Or make it 2 tubs of ${first.name}`;
    }

    this.querySelectorAll('[data-chip]').forEach((chip) => {
      if (!(chip instanceof HTMLButtonElement)) return;
      const id = Number(chip.dataset.chip);
      const on = this.picks.includes(id);
      chip.setAttribute('aria-pressed', String(on));
      if (chip.disabled) return;

      const full = picks === 2 && !on;
      if (full) chip.setAttribute('aria-disabled', 'true');
      else chip.removeAttribute('aria-disabled');

      const badge = chip.querySelector('.ora-tubs__badge');
      if (badge instanceof HTMLElement) {
        badge.toggleAttribute('data-plus', !on && picks === 1);
        badge.textContent = on ? (doubled ? '×2' : '✓') : picks === 1 ? '+' : '';
      }
    });
  }

  /**
   * Rewrites the purchase-option prices for the pair, or puts them back.
   * @param {1 | 2} tubs
   */
  renderOptions(tubs) {
    const options = this.options;
    if (!options) return;
    const subCard = options.querySelector('.ora-po__card--sub');
    const onceCard = options.querySelector('.ora-po__card--once');
    const subPrice = subCard?.querySelector('.ora-po__price');
    const oncePrice = onceCard?.querySelector('.ora-po__price');

    options.querySelectorAll('[data-ora-tubs-added]').forEach((el) => el.remove());

    if (tubs === 1) {
      if (this.saved) {
        if (subPrice && this.saved.sub !== undefined) subPrice.innerHTML = this.saved.sub;
        if (oncePrice && this.saved.once !== undefined) oncePrice.innerHTML = this.saved.once;
        const tag = subCard?.querySelector('.ora-po__tag');
        if (tag && this.saved.subTag) tag.textContent = this.saved.subTag;
        this.saved = null;
      }
      return;
    }

    if (!this.saved) {
      this.saved = {
        sub: subPrice?.innerHTML,
        once: oncePrice?.innerHTML,
        subTag: subCard?.querySelector('.ora-po__tag')?.textContent ?? null,
      };
    }

    const sub = this.sums('subscribe');
    const once = this.sums('once');

    if (subPrice) subPrice.innerHTML = this.priceHtml(sub.paid, sub.full);
    if (oncePrice) oncePrice.innerHTML = this.priceHtml(once.paid, once.full);

    if (subCard) {
      let tag = subCard.querySelector('.ora-po__tag');
      if (!tag) {
        tag = this.added('span', 'ora-po__tag');
        subCard.prepend(tag);
      }
      tag.textContent = `Save ${this.money(sub.save)}`;

      // The note explains the two-tub frequency, so it shows only while that
      // is the frequency chosen.
      const freq = subCard.querySelector('.ora-po__freq');
      const select = subCard.querySelector('select[data-plan]');
      const want = (this.dataset.plan || '').trim().toLowerCase();
      const onPlan =
        select instanceof HTMLSelectElement &&
        select.selectedOptions[0]?.text.trim().toLowerCase() === want;
      const note = this.fromTemplate('[data-freq-note]');
      if (freq && note && this.mode === 'subscribe' && onPlan) freq.after(note);
    }

    if (onceCard) {
      if (once.save > 0) {
        const tag = this.added('span', 'ora-po__tag');
        tag.textContent = `Save ${this.money(once.save)}`;
        onceCard.prepend(tag);
      }
      if (this.mode === 'once' && this.shipsFree('once', once.paid)) {
        const note = this.fromTemplate('[data-once-note]');
        if (note) onceCard.append(note);
      }
    }
  }

  /** @param {number} paid @param {number} full */
  priceHtml(paid, full) {
    const strong = document.createElement('strong');
    strong.textContent = this.money(paid);
    if (paid >= full) return strong.outerHTML;
    const was = document.createElement('s');
    was.textContent = this.money(full);
    return strong.outerHTML + was.outerHTML;
  }

  /**
   * @param {1 | 2} tubs
   * @param {'subscribe' | 'once'} mode
   * @param {number} picks
   */
  renderButton(tubs, mode, picks) {
    const button = this.addButton;
    const label = button?.querySelector('.add-to-cart-text__content > span');
    // The notes go under the whole button row, not into it: the row is a flex
    // line, and a note inside it sat beside the button and squeezed it.
    const holder = button?.closest('.product-form-buttons') || button;
    holder?.parentElement?.querySelectorAll(':scope > [data-ora-tubs-added]').forEach((el) => el.remove());

    if (tubs === 1) {
      if (label && this.labelBefore) label.textContent = this.labelBefore;
      this.labelBefore = '';
      return;
    }
    if (!label || !holder) return;
    if (!this.labelBefore) this.labelBefore = label.textContent || '';

    const { save } = this.sums(mode);
    if (picks === 2) {
      label.textContent = save > 0 ? `Add 2 tubs to cart · save ${this.money(save)}` : 'Add 2 tubs to cart';
      const note = this.fromTemplate('[data-cart-note]');
      if (note) holder.after(note);
    } else if (picks === 1) {
      label.textContent = 'Add 1 tub to cart';
      const hint = this.fromTemplate('[data-hint]');
      if (hint) {
        hint.textContent = (hint.textContent || '').replace('[save]', this.money(save));
        holder.after(hint);
      }
    } else {
      label.textContent = 'Pick 2 flavors';
    }
  }

  /** @param {string} tag @param {string} className */
  added(tag, className) {
    const el = document.createElement(tag);
    el.className = className;
    el.setAttribute('data-ora-tubs-added', '');
    return el;
  }

  /** @param {string} selector */
  fromTemplate(selector) {
    const template = this.querySelector(`template${selector}`);
    if (!(template instanceof HTMLTemplateElement)) return null;
    const el = template.content.firstElementChild?.cloneNode(true);
    if (!(el instanceof HTMLElement)) return null;
    el.setAttribute('data-ora-tubs-added', '');
    return el;
  }

  /* ---- the add ---- */

  /** @param {HTMLButtonElement} button */
  async add(button) {
    const mode = this.mode;
    const select = this.options?.querySelector('select[data-plan]');
    const plan = mode === 'subscribe' && select instanceof HTMLSelectElement ? Number(select.value) : 0;
    const pair = this.picks.length === 2;
    const bundle = this.dataset.bundle || '';

    /** @type {Array<Record<string, unknown>>} */
    let items;
    if (pair && this.picks[0] === this.picks[1]) {
      items = [{ id: this.picks[0], quantity: 2 }];
    } else {
      items = this.picks.map((id) => ({ id, quantity: 1 }));
    }
    items = items.map((item) => ({
      ...item,
      ...(plan ? { selling_plan: plan } : {}),
      ...(pair && bundle ? { properties: { _bundle: bundle } } : {}),
    }));

    const code = pair ? (this.dataset.code || '').trim() : '';

    this.busy = true;
    button.setAttribute('aria-busy', 'true');
    try {
      await changeCart({
        action: 'add',
        source: 'ora-tub-choice',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        write: async (sections) => {
          const added = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections }),
          }).then((r) => r.json());
          if (!code || added.status) return added;
          return this.applyCode(code, sections);
        },
      });
    } catch (error) {
      console.error(error);
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = 'That didn’t go through. Please try again.';
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Adds the code to whatever codes the cart already holds -- a partner's
   * link may have applied one, and the Cart API replaces the list it is sent.
   * @param {string} code
   * @param {string} sections
   */
  async applyCode(code, sections) {
    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
    const codes = (cart.discount_codes || []).map((/** @type {{ code: string }} */ d) => d.code);
    if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);
    return fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections }),
    }).then((r) => r.json());
  }
}

if (!customElements.get('ora-tub-choice')) {
  customElements.define('ora-tub-choice', OraTubChoice);
}
